import { useEffect, useRef, useState, type ReactNode } from 'react'
import { useSearchParams } from 'react-router-dom'
import { App as AntdApp, Button, Descriptions, Drawer, Form, Input, Modal, Select, Space, Table, Tag, Tooltip, Typography } from 'antd'
import { DownloadOutlined, EditOutlined, PlusOutlined, ReloadOutlined, SearchOutlined } from '@ant-design/icons'
import ErrorResult from '@/components/feedback/ErrorResult'
import { formatMessageTime } from '@/utils/datetime'
import {
  useExportGroupBindings,
  useCreateManualGroup,
  useGenerateZentaoProjectForBinding,
  useGroupBindingList,
  useRecommendGroupBindingProduct,
  useSuggestGroupBindingProjectName,
  useSyncZentaoMappingOptions,
  useUpdateGroupBinding,
  useUpdateGroupBindingServiceOwner,
  useZentaoMappingOptions,
} from '@/modules/groupBinding/queries'
import { useAuthStore } from '@/stores/authStore'
import type { GroupBindingItem, GroupBindingListParams, GroupBindingProductRecommendationResult, GroupBindingProjectCreateApplyItem, GroupBindingUpdatePayload, OwnerUserOption } from '@/types/groupBinding'

const { Text } = Typography
const DEFAULT_ZENTAO_PRODUCT_ID = 339

type SearchableSelectOption = {
  value: string
  label: string
  searchText: string
}

type CalibrationOption = {
  value: string
  label: string
  description: string
}

const CUSTOMER_LEVEL_OPTIONS: CalibrationOption[] = [
  { value: 'A', label: 'A 重点客户', description: '政务、医院、生产关键客户，问题优先处理，后续响应时限更紧' },
  { value: 'B', label: 'B 普通客户', description: '正常已交付客户，默认售后优先级' },
  { value: 'C', label: 'C 低优先级客户', description: '非核心、低频或低风险客户' },
  { value: 'trial', label: '试用/试运行', description: '试点、试用、UAT 或未完全转正式售后的客户' },
  { value: 'pending', label: '待定', description: '信息不足，需要人工继续校准' },
]

const BUSINESS_STAGE_OPTIONS: CalibrationOption[] = [
  { value: 'after_sale', label: '售后', description: '已上线或已交付后的真实售后主链' },
  { value: 'delivery', label: '交付中', description: '项目交付、部署、上线前后的问题' },
  { value: 'maintenance', label: '维保', description: '维保期、巡检、运维保障类问题' },
  { value: 'rectification', label: '整改专项', description: '专项整改、集中治理或验收整改类问题' },
  { value: 'trial', label: '试运行', description: '试点、试用、UAT 或灰度运行阶段' },
  { value: 'pending', label: '待定', description: '信息不足，需要人工继续校准' },
]

const CUSTOMER_LEVEL_ALIASES: Record<string, string> = {
  '': 'pending',
  '-': 'pending',
  none: 'pending',
  null: 'pending',
  unassigned: 'pending',
  '待定': 'pending',
  pending: 'pending',
  a: 'A',
  A: 'A',
  b: 'B',
  B: 'B',
  c: 'C',
  C: 'C',
  test: 'trial',
  trial: 'trial',
  '试用': 'trial',
  '试运行': 'trial',
}

const BUSINESS_STAGE_ALIASES: Record<string, string> = {
  '': 'pending',
  '-': 'pending',
  none: 'pending',
  null: 'pending',
  unassigned: 'pending',
  '待定': 'pending',
  pending: 'pending',
  after_sale: 'after_sale',
  'after-sale': 'after_sale',
  'after sale': 'after_sale',
  support: 'after_sale',
  '售后': 'after_sale',
  delivery: 'delivery',
  '交付': 'delivery',
  '交付中': 'delivery',
  maintenance: 'maintenance',
  '维保': 'maintenance',
  '维护': 'maintenance',
  rectification: 'rectification',
  '整改': 'rectification',
  '售后整改': 'rectification',
  trial: 'trial',
  test: 'trial',
  uat: 'trial',
  uat_probe: 'trial',
  '试用': 'trial',
  '试运行': 'trial',
}

function normalizeByAlias(value: string | null | undefined, aliases: Record<string, string>) {
  const rawValue = (value || '').trim()
  return aliases[rawValue] || aliases[rawValue.toLowerCase()] || rawValue
}

function normalizeCustomerLevel(value: string | null | undefined) {
  return normalizeByAlias(value, CUSTOMER_LEVEL_ALIASES)
}

function normalizeBusinessStage(value: string | null | undefined) {
  return normalizeByAlias(value, BUSINESS_STAGE_ALIASES)
}

type FormValidationError = {
  errorFields: Array<{
    name?: Array<string | number>
    errors?: string[]
  }>
}

function isFormValidationError(error: unknown): error is FormValidationError {
  return Boolean(error && typeof error === 'object' && 'errorFields' in error)
}

function isProjectNameValidationError(error: unknown) {
  return error instanceof Error && error.message === 'project_name does not follow ZenTao naming convention'
}

function buildCalibrationSelectOptions(options: CalibrationOption[]): SearchableSelectOption[] {
  return options.map((item) => ({
    value: item.value,
    label: item.label,
    searchText: [item.value, item.label, item.description].join(' ').toLowerCase(),
  }))
}

const CUSTOMER_LEVEL_SELECT_OPTIONS = buildCalibrationSelectOptions(CUSTOMER_LEVEL_OPTIONS)
const BUSINESS_STAGE_SELECT_OPTIONS = buildCalibrationSelectOptions(BUSINESS_STAGE_OPTIONS)

function filterSearchableOption(inputValue: string, option?: SearchableSelectOption) {
  const keyword = inputValue.trim().toLowerCase()
  if (!keyword) return true
  return (option?.searchText || option?.label || option?.value || '').toLowerCase().includes(keyword)
}

function statusTag(status: string) {
  if (!status) return <Text type="secondary">未同步</Text>
  const color = status === 'doing' ? 'processing' : status === 'closed' ? 'default' : 'warning'
  return <Tag color={color}>{status}</Tag>
}

export default function GroupBindingListPage() {
  const [searchParams, setSearchParams] = useSearchParams()
  const permissions = useAuthStore((state) => state.permissions)
  const actor = useAuthStore((state) => state.actor)
  const roles = useAuthStore((state) => state.roles)
  const canManageAllBindings = roles.includes('admin') || roles.includes('manager')
  const canCalibrateAnyBinding = canManageAllBindings || roles.includes('service_agent')
  const canCalibrateBinding = (binding: GroupBindingItem) =>
    canManageAllBindings || (roles.includes('service_agent') && binding.service_owner === actor)
  const canManageBindings = permissions.includes('binding:manage')
  const canCreateManualGroup = permissions.includes('binding:manual_create')
  const [page, setPage] = useState(1)
  const [pageSize, setPageSize] = useState(20)
  const [keyword, setKeyword] = useState('')
  const roomIdKeyword = searchParams.get('room_id') || ''
  const [serviceOwnerKeyword, setServiceOwnerKeyword] = useState('')
  const [activeFilter, setActiveFilter] = useState<boolean | undefined>()
  const [sortValue, setSortValue] = useState('created_at.desc')
  const [editing, setEditing] = useState<GroupBindingItem | null>(null)
  const [manualGroupModalOpen, setManualGroupModalOpen] = useState(false)
  const [productRecommendation, setProductRecommendation] = useState<GroupBindingProductRecommendationResult | null>(null)
  const [manualProductOverride, setManualProductOverride] = useState(false)
  const productRecommendationRequestId = useRef(0)
  const manualProductOverrideRef = useRef(false)
  const [form] = Form.useForm<GroupBindingUpdatePayload>()
  const [manualGroupForm] = Form.useForm<{ room_name: string }>()
  const { message } = AntdApp.useApp()
  const selectedProjectId = Form.useWatch('zentao_project_id', form)
  const selectedExecutionId = Form.useWatch('zentao_execution_id', form)
  const filterParams: Omit<GroupBindingListParams, 'page' | 'page_size'> = {
    keyword: keyword || undefined,
    room_id: roomIdKeyword.trim() || undefined,
    service_owner: serviceOwnerKeyword || undefined,
    is_active: activeFilter,
    sort_by: sortValue.split('.')[0],
    sort_order: sortValue.split('.')[1],
  }

  const { data, isLoading, isError, error, refetch } = useGroupBindingList({
    page,
    page_size: pageSize,
    ...filterParams,
  })
  const updateMutation = useUpdateGroupBinding()
  const serviceOwnerMutation = useUpdateGroupBindingServiceOwner()
  const createManualGroupMutation = useCreateManualGroup()
  const exportMutation = useExportGroupBindings()
  const syncOptionsMutation = useSyncZentaoMappingOptions()
  const suggestProjectNameMutation = useSuggestGroupBindingProjectName()
  const {
    mutateAsync: recommendProduct,
    isPending: isProductRecommendationPending,
  } = useRecommendGroupBindingProduct()
  const generateProjectMutation = useGenerateZentaoProjectForBinding()
  const {
    data: projectTreeOptions,
    isLoading: isProjectTreeLoading,
    refetch: refetchProjectTreeOptions,
  } = useZentaoMappingOptions(undefined, !!editing)
  const zentaoLiveAvailable = projectTreeOptions?.zentao_live_available ?? true

  useEffect(() => {
    if (!editing) return
    productRecommendationRequestId.current += 1
    const requestId = productRecommendationRequestId.current
    form.setFieldsValue({
      room_name: editing.room_name,
      customer_name: editing.customer_name,
      project_name: editing.project_name,
      service_owner: editing.service_owner,
      sales_owner: editing.sales_owner || '',
      tech_owner: editing.tech_owner,
      zentao_product_id: editing.zentao_product_id,
      zentao_project_id: editing.zentao_project_id,
      zentao_execution_id: editing.zentao_execution_id,
      execution_routing_mode: editing.execution_routing_mode || 'auto',
      customer_level: normalizeCustomerLevel(editing.customer_level),
      business_stage: normalizeBusinessStage(editing.business_stage),
      remark: editing.remark || '',
    })

    if (editing.zentao_product_id > 0 && editing.zentao_product_id !== DEFAULT_ZENTAO_PRODUCT_ID) return

    void recommendProduct({
      bindingId: editing.id,
      payload: {
        room_name: editing.room_name,
        customer_name: editing.customer_name,
        project_name: editing.project_name,
      },
    }).then((result) => {
      if (requestId !== productRecommendationRequestId.current || manualProductOverrideRef.current) return
      form.setFieldValue('zentao_product_id', result.product_id || DEFAULT_ZENTAO_PRODUCT_ID)
      setProductRecommendation(result)
    }).catch(() => {
      if (requestId !== productRecommendationRequestId.current || manualProductOverrideRef.current) return
      form.setFieldValue('zentao_product_id', DEFAULT_ZENTAO_PRODUCT_ID)
      setProductRecommendation({
        product_id: DEFAULT_ZENTAO_PRODUCT_ID,
        product_name: '售后-通用',
        source: 'default',
        confidence: 0,
        reason: '推荐服务暂不可用，已使用默认产品 339',
        fallback_used: true,
        warnings: [],
      })
    })
  }, [editing, form, recommendProduct])

  if (isError) return <ErrorResult error={error} onRetry={() => refetch()} />

  const closeDrawer = () => {
    productRecommendationRequestId.current += 1
    manualProductOverrideRef.current = false
    setEditing(null)
    setProductRecommendation(null)
    setManualProductOverride(false)
    form.resetFields()
  }

  const openDrawer = (binding: GroupBindingItem) => {
    productRecommendationRequestId.current += 1
    manualProductOverrideRef.current = false
    setProductRecommendation(null)
    setManualProductOverride(false)
    setEditing(binding)
  }

  const handleFormValidationError = (error: FormValidationError, fallbackMessage: string) => {
    const firstError = error.errorFields[0]
    const firstField = firstError?.name
    if (firstField) {
      form.scrollToField(firstField, { block: 'center' })
    }
    message.warning(firstError?.errors?.[0] || fallbackMessage)
  }

  const saveEditingForm = async ({ closeAfterSave, successMessage }: { closeAfterSave: boolean, successMessage?: string }) => {
    if (!editing) return
    try {
      const values = await form.validateFields()
      await updateMutation.mutateAsync({
        bindingId: editing.id,
        payload: {
          ...values,
          customer_level: normalizeCustomerLevel(values.customer_level),
          business_stage: normalizeBusinessStage(values.business_stage),
        },
      })
      if (successMessage) {
        message.success(successMessage)
      }
      if (closeAfterSave) {
        closeDrawer()
      }
      return values
    } catch (error) {
      if (isFormValidationError(error)) {
        handleFormValidationError(error, '请先补齐群映射表单必填项')
        return
      }
      if (isProjectNameValidationError(error)) {
        form.setFields([{
          name: 'project_name',
          errors: ['平台项目名称必须是客户/业主长期容器名，不能包含“项目、维保、售后、一期、2026”等阶段或时间词。'],
        }])
        message.error('平台项目名称不符合禅道命名规范')
        return
      }
      message.error(error instanceof Error ? error.message : '群映射保存失败')
    }
  }

  const submit = async () => {
    await saveEditingForm({ closeAfterSave: true, successMessage: '群映射已更新' })
  }

  const saveServiceOwnerOnly = async () => {
    if (!editing) return
    let selectedOwner: string
    try {
      const values = await form.validateFields(['service_owner'])
      selectedOwner = String(values.service_owner || '').trim()
    } catch (error) {
      if (isFormValidationError(error)) {
        message.warning(error.errorFields?.[0]?.errors?.[0] || '请先选择接待负责人')
      }
      return
    }
    if (!selectedOwner) {
      message.warning('请先选择接待负责人')
      return
    }
    try {
      await serviceOwnerMutation.mutateAsync({ bindingId: editing.id, serviceOwner: selectedOwner })
      setEditing({ ...editing, service_owner: selectedOwner })
      message.success('接待负责人已更新')
    } catch (error) {
      message.error(error instanceof Error ? error.message : '接待负责人更新失败')
    }
  }

  const submitManualGroup = async () => {
    try {
      const values = await manualGroupForm.validateFields()
      const created = await createManualGroupMutation.mutateAsync({ room_name: values.room_name.trim() })
      message.success(`手工群“${created.room_name}”已添加，请继续校准映射后录入消息`)
      manualGroupForm.resetFields()
      setManualGroupModalOpen(false)
      setPage(1)
      void refetch()
    } catch (error) {
      if (isFormValidationError(error)) return
      message.error(error instanceof Error ? error.message : '手工群添加失败')
    }
  }

  const exportCurrentFilter = async () => {
    try {
      await exportMutation.mutateAsync(filterParams)
      message.success('群绑定 Excel 已导出')
    } catch (error) {
      message.error(error instanceof Error ? error.message : '群绑定 Excel 导出失败')
    }
  }

  const updateRoomIdKeyword = (value: string) => {
    const trimmedValue = value.trim()
    setPage(1)

    const nextParams = new URLSearchParams(searchParams)
    if (trimmedValue) {
      nextParams.set('room_id', trimmedValue)
    } else {
      nextParams.delete('room_id')
    }
    setSearchParams(nextParams, { replace: true })
  }

  const syncMappingOptions = async () => {
    try {
      await syncOptionsMutation.mutateAsync()
      void refetchProjectTreeOptions()
      message.success('候选已同步并刷新')
    } catch (error) {
      message.error(error instanceof Error ? error.message : '候选同步失败')
    }
  }

  const suggestProjectName = async () => {
    try {
      const values = form.getFieldsValue(['room_name', 'customer_name', 'project_name'])
      const result = await suggestProjectNameMutation.mutateAsync({
        room_name: values.room_name,
        customer_name: values.customer_name,
        project_name: values.project_name,
      })
      if (!result.suggested_project_name) {
        message.warning(result.reason || '无法推荐项目名，请补充客户名或群名称')
        return
      }
      form.setFieldsValue({ project_name: result.suggested_project_name })
      form.setFields([{ name: 'project_name', errors: [] }])
      if (result.changed) {
        message.success(`已推荐项目名：${result.suggested_project_name}`)
        return
      }
      message.info('当前项目名已符合禅道命名规范')
    } catch (error) {
      message.error(error instanceof Error ? error.message : '推荐项目名失败')
    }
  }

  const generateZentaoProject = async () => {
    if (!editing) return
    if (Number(selectedProjectId || 0) > 0 || editing.zentao_project_id > 0) {
      message.info('当前群已经绑定禅道项目')
      return
    }
    try {
      const values = await form.validateFields(['room_name', 'customer_name', 'project_name', 'zentao_product_id'])
      const zentaoProductId = Number(values.zentao_product_id || 0)
      if (zentaoProductId <= 0) {
        message.warning('请先选择禅道产品')
        return
      }
      const result = await generateProjectMutation.mutateAsync({
        bindingId: editing.id,
        zentaoProductId,
        currentValues: {
          room_name: values.room_name,
          customer_name: values.customer_name,
          project_name: values.project_name,
        },
      })
      const item = result.items[0] as GroupBindingProjectCreateApplyItem | undefined
      if (item?.status === 'project_created' || item?.status === 'already_bound') {
        form.setFieldsValue({
          zentao_product_id: item.zentao_product_id || zentaoProductId,
          zentao_project_id: item.zentao_project_id,
          zentao_execution_id: item.zentao_execution_id || 0,
          execution_routing_mode: item.execution_routing_mode || 'auto',
        })
        void refetch()
        void refetchProjectTreeOptions()
        message.success(`禅道项目已生成并绑定 #${item.zentao_project_id}`)
        return
      }
      message.warning(item?.reason || '未生成禅道项目，请检查候选状态')
    } catch (error) {
      if (isFormValidationError(error)) {
        handleFormValidationError(error, '请先补齐生成禅道项目所需字段')
        return
      }
      message.error(error instanceof Error ? error.message : '生成禅道项目失败')
    }
  }

  const productOptions = (projectTreeOptions?.product_tree || []).map((product) => ({
    value: product.product_id,
    label: `${product.name} #${product.product_id} ${product.status}`,
  }))
  if (!productOptions.some((option) => option.value === DEFAULT_ZENTAO_PRODUCT_ID)) {
    productOptions.push({ value: DEFAULT_ZENTAO_PRODUCT_ID, label: '售后-通用 #339 fallback' })
  }
  const syncActionDisabledReason = !zentaoLiveAvailable
      ? '当前禅道实时连接不可用，暂不能同步候选信息。'
      : undefined
  const generateActionDisabledReason = !canManageBindings
    ? '当前账号只有群映射校准权限，生成禅道项目需管理员或经理账号。'
    : !zentaoLiveAvailable
      ? '当前禅道实时连接不可用，暂不能生成禅道项目。'
      : undefined
  const renderActionButton = (button: ReactNode, disabledReason?: string) => {
    if (!disabledReason) return button
    return (
      <Tooltip title={disabledReason} placement="left">
        <span style={{ display: 'block' }}>{button}</span>
      </Tooltip>
    )
  }

  const allExecutions = [...(projectTreeOptions?.executions || [])]
  if (editing && editing.zentao_execution_id && !allExecutions.some((item) => item.execution_id === editing.zentao_execution_id)) {
    allExecutions.push({
      execution_id: editing.zentao_execution_id,
      project_id: editing.zentao_project_id,
      name: editing.zentao_execution_name || '未同步',
      status: editing.zentao_execution_status || '未同步',
      begin: null,
      end: null,
    })
  }
  const projectOptions = (projectTreeOptions?.projects || []).map((project) => ({
    value: project.project_id,
    label: `${project.name} #${project.project_id} ${project.status}`,
  }))
  const selectedExecution = allExecutions.find((execution) => execution.execution_id === selectedExecutionId)
  const defaultExecutionOptions = allExecutions
    .filter((execution) => !selectedProjectId || execution.project_id === selectedProjectId)
    .map((execution) => ({
    value: execution.execution_id,
    label: `${execution.name} #${execution.execution_id} ${execution.status}`,
  }))
  const buildOwnerOptions = (users: OwnerUserOption[] | undefined, currentValue?: string | null) => {
    const seen = new Set<string>()
    const options = (users || []).flatMap((user) => {
      const account = user.account.trim()
      if (!account) return []
      seen.add(account)
      const name = user.realname || account
      const labelBase = name === account ? account : `${name} (${account})`
      const meta = [user.dept, user.role, user.status].filter(Boolean).join(' ')
      return [{
        value: account,
        label: `${labelBase}${meta ? ` ${meta}` : ''}`,
        searchText: [account, name, user.realname, user.dept, user.role, user.status].filter(Boolean).join(' ').toLowerCase(),
      }]
    })
    const current = (currentValue || '').trim()
    if (current && !seen.has(current)) {
      options.unshift({ value: current, label: current, searchText: current.toLowerCase() })
    }
    return options
  }
  const serviceOwnerOptions = buildOwnerOptions(projectTreeOptions?.service_owner_options, editing?.service_owner)
  const techOwnerOptions = buildOwnerOptions(projectTreeOptions?.tech_owner_options, editing?.tech_owner)
  const getDrawerPopupContainer = (triggerNode: HTMLElement) =>
    (triggerNode.closest('.ant-drawer-content') as HTMLElement | null) || document.body
  const columns = [
    { title: '产生时间', dataIndex: 'created_at', width: 120, render: (value: string) => formatMessageTime(value) },
    {
      title: '群聊',
      dataIndex: 'room_name',
      width: 220,
      render: (value: string, row: GroupBindingItem) => (
        <Space orientation="vertical" size={0}>
          <Space size={4}>
            <Text strong>{value}</Text>
            <Tag color={row.group_source === 'manual' ? 'blue' : 'green'}>
              {row.group_source === 'manual' ? '手工群' : '企业微信群'}
            </Tag>
          </Space>
          <Text type="secondary" style={{ fontSize: 12 }}>{row.room_id}</Text>
        </Space>
      ),
    },
    { title: '客户', dataIndex: 'customer_name', width: 120 },
    { title: '平台项目', dataIndex: 'project_name', width: 140 },
    {
      title: '禅道产品',
      dataIndex: 'zentao_product_id',
      width: 110,
      render: (value: number) => value > 0 ? <Text>#{value}</Text> : <Text type="secondary">待审核</Text>,
    },
    {
      title: '禅道项目',
      width: 190,
      render: (_: unknown, row: GroupBindingItem) => (
        <Space orientation="vertical" size={0}>
          <Text>{row.zentao_project_name || `#${row.zentao_project_id}`}</Text>
          <Text type="secondary" style={{ fontSize: 12 }}>#{row.zentao_project_id} {statusTag(row.zentao_project_status)}</Text>
        </Space>
      ),
    },
    {
      title: '禅道执行',
      width: 180,
      render: (_: unknown, row: GroupBindingItem) => (
        <Space orientation="vertical" size={0}>
          <Text>{row.zentao_execution_name || `#${row.zentao_execution_id}`}</Text>
          <Text type="secondary" style={{ fontSize: 12 }}>#{row.zentao_execution_id} {statusTag(row.zentao_execution_status)}</Text>
        </Space>
      ),
    },
    { title: '接待负责人', dataIndex: 'service_owner', width: 100 },
    { title: '默认首派研发', dataIndex: 'tech_owner', width: 110 },
    {
      title: '校验',
      dataIndex: 'validated',
      width: 80,
      render: (value: boolean) => <Tag color={value ? 'success' : 'warning'}>{value ? '已验证' : '待验证'}</Tag>,
    },
    ...(canCalibrateAnyBinding
      ? [{
          title: '操作',
          width: 90,
          fixed: 'right' as const,
          render: (_: unknown, row: GroupBindingItem) => canCalibrateBinding(row) ? (
            <Button type="link" size="small" icon={<EditOutlined />} onClick={() => openDrawer(row)}>校准</Button>
          ) : <Text type="secondary">仅可查看</Text>,
        }]
      : []),
  ]

  return (
    <div data-testid="group-bindings-page" style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      <Space wrap>
        <Input
          placeholder="搜索群/客户/项目/执行"
          prefix={<SearchOutlined />}
          allowClear
          style={{ width: 280 }}
          value={keyword}
          onChange={(event) => { setKeyword(event.target.value); setPage(1) }}
        />
        <Input
          placeholder="搜索群 ID / room_id"
          prefix={<SearchOutlined />}
          allowClear
          style={{ width: 300 }}
          value={roomIdKeyword}
          onChange={(event) => updateRoomIdKeyword(event.target.value)}
          data-testid="group-binding-room-id-filter"
        />
        <Input
          placeholder="接待账号 / unassigned"
          prefix={<SearchOutlined />}
          allowClear
          style={{ width: 220 }}
          value={serviceOwnerKeyword}
          onChange={(event) => { setServiceOwnerKeyword(event.target.value); setPage(1) }}
        />
        <Select
          style={{ width: 140 }}
          value={activeFilter}
          onChange={(value) => { setActiveFilter(value); setPage(1) }}
          options={[
            { label: '启用映射', value: true },
            { label: '停用映射', value: false },
          ]}
          allowClear
          placeholder="映射状态"
        />
        <Button icon={<ReloadOutlined />} onClick={() => refetch()}>刷新</Button>
        <Select
          aria-label="排序"
          style={{ width: 180 }}
          value={sortValue}
          onChange={(value) => { setSortValue(value); setPage(1) }}
          options={[
            { label: '最新产生在前', value: 'created_at.desc' },
            { label: '最早产生在前', value: 'created_at.asc' },
            { label: '最近更新在前', value: 'updated_at.desc' },
            { label: '群名 A-Z', value: 'room_name.asc' },
            { label: '客户名 A-Z', value: 'customer_name.asc' },
          ]}
        />
        {permissions.includes('binding:read') && (
          <Button
            icon={<DownloadOutlined />}
            loading={exportMutation.isPending}
            onClick={exportCurrentFilter}
            data-testid="group-binding-export-button"
          >
            导出Excel
          </Button>
        )}
        {canCreateManualGroup && (
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={() => setManualGroupModalOpen(true)}
            data-testid="group-binding-add-manual-button"
          >
            添加手工群
          </Button>
        )}
      </Space>

      <Modal
        title="添加手工群"
        open={manualGroupModalOpen}
        onOk={() => void submitManualGroup()}
        onCancel={() => {
          manualGroupForm.resetFields()
          setManualGroupModalOpen(false)
        }}
        confirmLoading={createManualGroupMutation.isPending}
        okText="添加并进入待校准"
        destroyOnHidden
      >
        <Typography.Paragraph type="secondary">
          手工群由售后人员维护，不从企业微信同步；完成群映射校准并启用后，可在消息页人工录入消息，其余处理流程与企业微信群一致。
        </Typography.Paragraph>
        <Form form={manualGroupForm} layout="vertical">
          <Form.Item
            name="room_name"
            label="群名称"
            rules={[{ required: true, whitespace: true, message: '请输入群名称' }]}
          >
            <Input placeholder="例如：某医院售后服务群" maxLength={255} autoFocus />
          </Form.Item>
        </Form>
      </Modal>

      <Table
        size="small"
        rowKey="id"
        loading={isLoading}
        dataSource={data?.items || []}
        pagination={{
          current: page,
          pageSize,
          total: data?.total || 0,
          showSizeChanger: true,
          showTotal: (total) => `共 ${total} 条`,
          onChange: (nextPage, nextPageSize) => { setPage(nextPage); setPageSize(nextPageSize) },
        }}
        scroll={{ x: 1350 }}
        columns={columns}
      />

      {editing && canCalibrateBinding(editing) && (
        <Drawer
          title="校准群映射"
          size="large"
          rootClassName="group-binding-calibration-drawer"
          open={!!editing}
          onClose={closeDrawer}
        >
          {editing && (
          <>
            <Descriptions size="small" column={1} style={{ marginBottom: 16 }}>
              <Descriptions.Item label="room_id">{editing.room_id}</Descriptions.Item>
              <Descriptions.Item label="当前禅道产品">#{editing.zentao_product_id || '待审核'}</Descriptions.Item>
              <Descriptions.Item label="当前禅道项目">{editing.zentao_project_name || '-'} #{editing.zentao_project_id}</Descriptions.Item>
              <Descriptions.Item label="默认执行/兼容兜底">{editing.zentao_execution_name || '-'} #{editing.default_execution_id || editing.zentao_execution_id || '未设置'}</Descriptions.Item>
            </Descriptions>
            <div className="group-binding-drawer-actions">
              <Button block type="primary" loading={updateMutation.isPending} onClick={submit}>保存</Button>
              <Button
                block
                loading={serviceOwnerMutation.isPending}
                onClick={saveServiceOwnerOnly}
                data-testid="save-service-owner-button"
              >
                仅保存接待负责人
              </Button>
              {canCalibrateBinding(editing) && renderActionButton(
                <Button
                  block
                  icon={<ReloadOutlined />}
                  loading={syncOptionsMutation.isPending}
                  onClick={syncMappingOptions}
                  data-testid="sync-mapping-options-button"
                  disabled={Boolean(syncActionDisabledReason)}
                >
                  禅道信息同步
                </Button>
              , syncActionDisabledReason)}
              {canCalibrateBinding(editing) && renderActionButton(
                <Button
                  block
                  loading={generateProjectMutation.isPending}
                  disabled={!zentaoLiveAvailable || Number(selectedProjectId || editing.zentao_project_id || 0) > 0}
                  onClick={generateZentaoProject}
                  data-testid="generate-zentao-project-button"
                >
                  生成禅道项目
                </Button>
              , !zentaoLiveAvailable ? generateActionDisabledReason : undefined)}
              <Button block onClick={closeDrawer}>取消</Button>
            </div>
            <Form form={form} layout="vertical">
              <Form.Item name="room_name" label="群名称" rules={[{ required: true, message: '请输入群名称' }]}>
                <Input />
              </Form.Item>
              <Form.Item name="customer_name" label="客户名称" rules={[{ required: true, message: '请输入客户名称' }]}>
                <Input />
              </Form.Item>
              <Form.Item name="project_name" label="平台项目名称" rules={[{ required: true, message: '请输入平台项目名称' }]}>
                <Input.Search
                  enterButton="推荐"
                  loading={suggestProjectNameMutation.isPending}
                  onSearch={() => { void suggestProjectName() }}
                  data-testid="project-name-input"
                />
              </Form.Item>
              <Form.Item
                name="zentao_product_id"
                label="禅道产品（审核）"
                extra={(
                  <>
                    <Text type="secondary">系统会根据群名、客户和项目自动推荐，您仍可在下拉框中修正。</Text>
                    <br />
                    {isProductRecommendationPending && <Text type="secondary">正在推荐禅道产品…</Text>}
                    {!isProductRecommendationPending && manualProductOverride && <Tag color="blue">已手工调整</Tag>}
                    {!isProductRecommendationPending && !manualProductOverride && productRecommendation && (
                      <Tag color={productRecommendation.fallback_used ? 'default' : 'green'}>
                        {productRecommendation.fallback_used ? '默认产品 339' : `自动推荐 · ${productRecommendation.source}`}
                      </Tag>
                    )}
                    {!isProductRecommendationPending && !manualProductOverride && productRecommendation?.reason && (
                      <Text type="secondary"> {productRecommendation.reason}</Text>
                    )}
                    {!zentaoLiveAvailable && (
                      <>
                        <br />
                        <Text type="secondary">当前禅道实时产品不可用，已回退到售后通用产品兜底。</Text>
                      </>
                    )}
                  </>
                )}
                rules={[
                  { required: true, message: '请选择禅道产品' },
                  { type: 'number', min: 1, message: '请选择有效禅道产品' },
                ]}
              >
                <Select
                  showSearch
                  allowClear
                  loading={isProjectTreeLoading}
                  optionFilterProp="label"
                  options={productOptions}
                  onChange={() => {
                    manualProductOverrideRef.current = true
                    setManualProductOverride(true)
                  }}
                  style={{ width: '100%' }}
                  placeholder="选择禅道产品"
                  data-testid="zentao-product-select"
                  getPopupContainer={getDrawerPopupContainer}
                  styles={{ popup: { root: { zIndex: 2000 } } }}
                />
              </Form.Item>
              <Form.Item
                name="zentao_project_id"
                label="禅道项目"
                extra={<Text type="secondary">选择客户长期项目；执行将在建单阶段按草稿内容解析</Text>}
                rules={[
                  { required: true, message: '请选择禅道项目' },
                  { type: 'number', min: 1, message: '请选择有效禅道项目' },
                ]}
              >
                <Select
                  showSearch
                  loading={isProjectTreeLoading}
                  optionFilterProp="label"
                  options={projectOptions}
                  placeholder="选择禅道项目"
                  getPopupContainer={getDrawerPopupContainer}
                  onChange={(projectId) => {
                    if (selectedExecution && selectedExecution.project_id !== projectId) {
                      form.setFieldValue('zentao_execution_id', 0)
                    }
                  }}
                />
              </Form.Item>
              <Form.Item name="execution_routing_mode" hidden>
                <Input />
              </Form.Item>
              <Form.Item
                name="zentao_execution_id"
                label="默认执行/兼容兜底"
                extra={<Text type="secondary">可不设置；建单时会按子系统、周期或专项选择/创建执行</Text>}
              >
                <Select
                  showSearch
                  allowClear
                  loading={isProjectTreeLoading}
                  optionFilterProp="label"
                  options={[{ value: 0, label: '不设置默认执行' }, ...defaultExecutionOptions]}
                  placeholder="可选默认执行"
                  getPopupContainer={getDrawerPopupContainer}
                  onClear={() => form.setFieldValue('zentao_execution_id', 0)}
                />
              </Form.Item>
              <Space style={{ width: '100%' }} size={12} align="start">
                <Form.Item
                  name="service_owner"
                  label="接待负责人"
                  extra={<Text type="secondary">换人可点下方“仅保存接待负责人”，只改这一项，不校验其他字段。</Text>}
                  rules={[{ required: true, message: '请输入接待负责人' }]}
                >
                  <Select
                    showSearch
                    allowClear
                    loading={isProjectTreeLoading}
                    style={{ width: 240 }}
                    options={serviceOwnerOptions}
                    optionFilterProp="searchText"
                    filterOption={filterSearchableOption}
                    virtual={false}
                    listHeight={360}
                    placeholder="选择平台人员账号"
                    getPopupContainer={getDrawerPopupContainer}
                    data-testid="service-owner-select"
                  />
                </Form.Item>
                <Form.Item name="tech_owner" label="默认首派研发" rules={[{ required: true, message: '请输入默认首派研发' }]}>
                  <Select
                    showSearch
                    allowClear
                    loading={isProjectTreeLoading}
                    style={{ width: 260 }}
                    options={techOwnerOptions}
                    optionFilterProp="searchText"
                    filterOption={filterSearchableOption}
                    virtual={false}
                    listHeight={360}
                    placeholder="选择禅道研发账号"
                    getPopupContainer={getDrawerPopupContainer}
                    data-testid="tech-owner-select"
                  />
                </Form.Item>
              </Space>
              <Space style={{ width: '100%' }} size={12} align="start">
                <Form.Item name="sales_owner" label="销售负责人">
                  <Input style={{ width: 180 }} />
                </Form.Item>
                <Form.Item
                  name="customer_level"
                  label="客户级别"
                  extra={<Text type="secondary">A=重点客户；B=普通已交付客户；C=低优先级客户；试用/试运行=试点或 UAT；待定=信息不足，需要继续人工校准。</Text>}
                  rules={[{ required: true, message: '请选择客户级别' }]}
                >
                  <Select
                    showSearch
                    style={{ width: 220 }}
                    options={CUSTOMER_LEVEL_SELECT_OPTIONS}
                    optionFilterProp="searchText"
                    filterOption={filterSearchableOption}
                    placeholder="选择客户级别"
                    getPopupContainer={getDrawerPopupContainer}
                    data-testid="customer-level-select"
                  />
                </Form.Item>
              </Space>
              <Form.Item
                name="business_stage"
                label="业务阶段"
                extra={<Text type="secondary">售后=已上线/已交付后的主链；交付中=部署上线相关；维保=巡检运维保障；整改专项=集中整改；试运行=试点、试用或 UAT；待定=信息不足。</Text>}
                rules={[{ required: true, message: '请选择业务阶段' }]}
              >
                <Select
                  showSearch
                  options={BUSINESS_STAGE_SELECT_OPTIONS}
                  optionFilterProp="searchText"
                  filterOption={filterSearchableOption}
                  placeholder="选择业务阶段"
                  getPopupContainer={getDrawerPopupContainer}
                  data-testid="business-stage-select"
                />
              </Form.Item>
              <Form.Item name="remark" label="备注">
                <Input.TextArea rows={3} />
              </Form.Item>
            </Form>
          </>
          )}
        </Drawer>
      )}
    </div>
  )
}
